import React, { useEffect, useState } from "react";
import { flagUrl, flagEmoji } from "@/data/countries";

/**
 * <Flag /> — resilient country-flag renderer.
 *
 * Behaviour:
 *   1. Renders <img src={flagUrl(code)}> pointing at our backend
 *      proxy (which caches to disk + falls back to a second CDN).
 *   2. If the img still fails (proxy down, malformed code, offline)
 *      we swap to the regional-indicator flag emoji so kids never
 *      see a broken-image icon.
 *
 * Feb 2026 cycle 43 — introduced after the "Match Flags cards are
 * blank" report; flagcdn.com was slow / rate-limited from many
 * browsers, causing intermittent blanks across the site.
 *
 * Prop contract:
 *   code       — ISO-3166 alpha-2, case-insensitive. `""` / null → emoji-only.
 *   className  — optional Tailwind classes applied to BOTH the img
 *                and the emoji fallback (dimensions, radius, ring).
 *   alt        — optional accessible label; falls back to
 *                `"Flag of <CODE>"`.
 *   "data-testid" — passthrough so downstream tests can select it.
 */
export default function Flag({
  code,
  className = "",
  alt,
  loading = "lazy",
  ...rest
}) {
  const [failed, setFailed] = useState(false);
  // A code change resets the failure state so the next flag gets a
  // fresh network attempt — parent may reuse this component in lists.
  useEffect(() => { setFailed(false); }, [code]);

  const normalised = typeof code === "string" ? code.trim().toLowerCase() : "";
  const validShape = /^[a-z]{2,3}$/.test(normalised);

  if (!validShape) {
    return (
      <span
        aria-label={alt || "Unknown flag"}
        role="img"
        className={`inline-flex items-center justify-center bg-slate-100 ${className}`}
        {...rest}
      >
        🌍
      </span>
    );
  }

  if (failed) {
    // Emoji fallback — regional-indicator pair, always available.
    return (
      <span
        aria-label={alt || `Flag of ${normalised.toUpperCase()}`}
        role="img"
        className={`inline-flex items-center justify-center bg-slate-100 text-3xl leading-none ${className}`}
        {...rest}
      >
        {flagEmoji(normalised)}
      </span>
    );
  }

  return (
    <img
      src={flagUrl(normalised)}
      alt={alt || `Flag of ${normalised.toUpperCase()}`}
      loading={loading}
      onError={() => setFailed(true)}
      className={className}
      {...rest}
    />
  );
}
